import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { DomainEntity } from '../../domain';
import { Repository } from '../repository';
import { Actives, Filter, FilterOp, PageData, SortMode, SortOrder } from '../search-options';
import { AnimalIdReconcilable, type AnimalIdRemap, reconcileAnimalReferences } from './animal-reference.reconciler';
import { SynchronizationService } from './synchronization.service';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export abstract class BaseIndexedDbRepository<S, T extends DomainEntity> implements Repository<S, T>, AnimalIdReconcilable {

    protected abstract readonly storeName: string;
    private animalIndexes: string[] = [];

    protected abstract mapToSummary(item: T): S;
    protected abstract getSearchableFields(): string[];
    protected abstract getResourcePath(): string;
    protected abstract mapToEntity(dbItem: T): T;

    protected constructor(
        protected readonly dbService: NgxIndexedDBService,
        protected readonly synchronizationService: SynchronizationService
    ) {}

    protected registerAnimalReferences(...indexes: [string, ...string[]]): void {
        this.animalIndexes = indexes;
        this.synchronizationService.registerAnimalIdReconciler(this);
    }

    public reconcileAnimalId(remap: AnimalIdRemap): Observable<void> {
        return reconcileAnimalReferences(this.dbService, this.storeName, this.animalIndexes, remap);
    }

    public saveLocally(values: T[]): Observable<T[]> {
        if (!values?.length) return of([]);

        const valuesToStore = values.map(value =>
            typeof (value as any).toJson === 'function' ? (value as any).toJson() : value
        );

        return this.dbService.bulkPut<T>(this.storeName, valuesToStore).pipe(
            map(() => values),
            catchError(err => {
                console.error('Erro na inserção local por lote:', err);
                return throwError(() => err);
            })
        );
    }

    public find(id: string): Observable<T | undefined> {
        return this.dbService.getByKey<T>(this.storeName, id).pipe(
            catchError(err => throwError(() => new Error(`Error finding item ${id} in ${this.storeName}: ${err.message || err}`)))
        );
    }

    public findBy(id: string): Observable<T | undefined> {
        return this.dbService.getByKey<T>(this.storeName, id).pipe(
            map(dbItem => dbItem ? this.mapToEntity(dbItem) : undefined),
            catchError(err => throwError(() => new Error(`Error finding item ${id} in ${this.storeName}: ${err.message || err}`)))
        );
    }

    public findSummary(id: string): Observable<S | undefined> {
        return this.dbService.getByKey<T>(this.storeName, id).pipe(
            map(dbItem => dbItem ? this.mapToSummary(dbItem) : undefined),
            catchError(err => throwError(() => new Error(`Error finding item ${id} in ${this.storeName}: ${err.message || err}`)))
        );
    }

    public list(query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<S[]> {
        return this.executeIndexedDbQuery({ query, filters, sortOrders, actives }).pipe(
            map(results => results.map(item => this.mapToSummary(item)))
        );
    }

    public listFull(query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<T[]> {
        return this.executeIndexedDbQuery({ query, filters, sortOrders, actives });
    }

    public page(pageData: PageData, query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<S[]> {
        return this.executeIndexedDbQuery({ query, filters, sortOrders, actives, pageData }).pipe(
            map(results => results.map(item => this.mapToSummary(item)))
        );
    }

    public pageFull(pageData: PageData, query?: string, filters?: Filter[], sortOrders?: SortOrder[], actives?: Actives): Observable<T[]> {
        return this.executeIndexedDbQuery({ query, filters, sortOrders, actives, pageData });
    }

    public count(query?: string, filters?: Filter[], actives?: Actives): Observable<number> {
        return this.executeIndexedDbQuery({ query, filters, actives, skipSortAndPage: true }).pipe(
            map(results => results.length)
        );
    }

    public insert(value: T): Observable<T> {
        const valueToStore = typeof (value as any).toJson === 'function' ? (value as any).toJson() : value;

        return this.dbService.add<T>(this.storeName, valueToStore).pipe(
            switchMap(addedItem => {
                const savedItem = typeof addedItem === 'object' ? addedItem : value;
                this.synchronizationService.attemptSync<T>({
                    method: 'POST',
                    resourcePath: this.getResourcePath(),
                    payload: savedItem
                }, this.storeName).subscribe({ error: err => {} });
                return of(savedItem as T);
            }),
            catchError(err => throwError(() => new Error(`Error inserting into ${this.storeName}: ${err.message || err}`)))
        );
    }


    public update(value: T): Observable<T> {
        const entityId = value.id;
        if (!entityId) return throwError(() => new Error(`Cannot update item in ${this.storeName} without an ID.`));

        const valueToStore = typeof (value as any).toJson === 'function' ? (value as any).toJson() : value;

        return this.dbService.update<T>(this.storeName, valueToStore).pipe(
            switchMap(updatedItem => {
                if (!updatedItem) { throw new Error(`Update in ${this.storeName} for id ${entityId} failed locally or item not found.`); }
                this.synchronizationService.attemptSync<T>({
                    method: 'PUT',
                    resourcePath: this.getResourcePath(),
                    resourceId: entityId,
                    payload: updatedItem
                }, this.storeName).subscribe({ error: err => {} });
                return of(updatedItem as T);
            }),
            catchError(err => {
                if (err instanceof Error && err.message.includes('failed locally')) { return throwError(() => err); }
                return throwError(() => new Error(`Error updating item ${entityId} in ${this.storeName}: ${err.message || err}`));
            })
        );
    }

    public activate(value: T): Observable<T> {
         const entityId = value.id;
         if (!entityId) { return throwError(() => new Error(`Cannot activate item in ${this.storeName} without an ID.`)); }
         if (typeof value.isActive === 'undefined') { return throwError(() => new Error(`Cannot activate item in ${this.storeName}: item does not have 'isActive' property.`)); }
         const valueToActivate = { ...value, isActive: true };
         return this.update(valueToActivate);
    }

    public delete(value: T, temp: boolean): Observable<boolean> {
        const entityId = value.id;
        if (!entityId) return throwError(() => new Error(`Cannot delete item in ${this.storeName} without an ID.`));

        const resourcePath = this.getResourcePath();

        if (temp) {
            if (typeof value.isActive === 'undefined') return throwError(() => new Error(`Cannot soft delete: item does not have 'isActive'.`));
            const valueToDeactivate = { ...value, isActive: false };
            return this.update(valueToDeactivate).pipe(
                map(() => true),
                catchError(err => throwError(() => new Error(`Soft delete failed for ${entityId}: ${err.message || err}`)))
            );
        } else {
            return this.dbService.delete(this.storeName, entityId).pipe(
                 map(result => Array.isArray(result) ? result.every(r => r) : !!result),
                 switchMap(localSuccess => {
                     if (!localSuccess) { throw new Error('Local hard delete failed'); }
                     this.synchronizationService.attemptSync<void>({
                         method: 'DELETE',
                         resourcePath: resourcePath,
                         resourceId: entityId
                     }, this.storeName).subscribe({ error: err => {} });
                     return of(true);
                 }),
                 catchError(err => throwError(() => new Error(`Hard delete operation failed for ${entityId}: ${err.message || err}`)))
            );
        }
    }

    protected executeIndexedDbQuery(options: {
        query?: string, filters?: Filter[], sortOrders?: SortOrder[],
        actives?: Actives, pageData?: PageData, skipSortAndPage?: boolean
    }): Observable<T[]> {
        return this.dbService.getAll<T>(this.storeName).pipe(
            map((items: T[]) => {
                let filteredItems: T[] = [...items];
                filteredItems = this.applyActivesFilter(filteredItems, options.actives);
                filteredItems = this.applyStructuredFilters(filteredItems, options.filters);
                filteredItems = this.applyTextQueryFilter(filteredItems, options.query);

                if (options.skipSortAndPage) { return filteredItems; }

                const sortedItems: T[] = this.applySorting(filteredItems, options.sortOrders);
                const paginatedItems: T[] = this.applyPagination(sortedItems, options.pageData);
                return paginatedItems;
            }),
            catchError((err: any) => throwError(() => new Error(`Error querying ${this.storeName}: ${err.message || err}`)))
        );
    }

    protected applyActivesFilter(items: T[], actives?: Actives): T[] {
        if (!actives || actives === Actives.ALL) {
            return items;
        }

        if (actives === Actives.TRUE) {
            return items.filter(item => item.isActive !== false);
        }

        if (actives === Actives.FALSE) {
            return items.filter(item => item.isActive === false);
        }

        return items;
    }

    protected applyTextQueryFilter(items: T[], query?: string): T[] {
        if (!query) return items;

        const queryLower = query.toLowerCase().trim();
        if (!queryLower) return items;

        const searchableFields = this.getSearchableFields();
        return items.filter(item =>
            searchableFields.some(field => {
                const value = this.getPropertyByPath(item, field);
                return typeof value === 'string' && value.toLowerCase().includes(queryLower);
            })
        );
    }

    protected applyStructuredFilters(items: T[], filters?: Filter[]): T[] {
        if (!filters || filters.length === 0) { return items; }
        return items.filter(item =>
            filters.every(filter => this.evaluateFilter(item, filter))
        );
    }

    protected applySorting(items: T[], sortOrders?: SortOrder[]): T[] {
        if (!sortOrders || sortOrders.length === 0) { return items; }
        return [...items].sort((a, b) => {
            for (const sort of sortOrders) {
                const valA = this.getPropertyByPath(a, sort.field);
                const valB = this.getPropertyByPath(b, sort.field);
                if (valA == null && valB == null) continue;
                if (valA == null) return 1;
                if (valB == null) return -1;
                let comparison: number;
                if (typeof valA === 'string' && typeof valB === 'string') {
                    comparison = valA.localeCompare(valB, 'pt-BR', { sensitivity: 'base' });
                } else if (valA instanceof Date && valB instanceof Date) {
                     comparison = valA.getTime() - valB.getTime();
                } else {
                    comparison = (valA < valB) ? -1 : (valA > valB) ? 1 : 0;
                }
                if (comparison !== 0) {
                    return sort.sortMode === SortMode.ASC ? comparison : -comparison;
                }
            } return 0;
        });
    }

    protected applyPagination(items: T[], pageData?: PageData): T[] {
        if (!pageData || !pageData.enabled) { return items; }
        const startIndex = pageData.first >= 0 ? pageData.first : 0;
        const rowCount = pageData.rows;
        const endIndex = startIndex + rowCount;
        return items.slice(startIndex, endIndex);
    }

    protected evaluateFilter(item: T, filter: Filter): boolean {
        const itemValue: any = this.getPropertyByPath(item, filter.field);
        const filterValue: any = filter.value;
        if (filter.operator === FilterOp.IN) {
            return Array.isArray(filterValue) && filterValue.includes(itemValue);
        }
        if (itemValue === undefined || itemValue === null) {
            return filter.operator === FilterOp.EQ
                && itemValue === filterValue
                || filter.operator === FilterOp.NE
                && itemValue !== filterValue;
        }
        switch (filter.operator) {
            case FilterOp.EQ:
                return itemValue == filterValue;
            case FilterOp.NE:
                return itemValue != filterValue;
            case FilterOp.GT:
                return itemValue > filterValue;
            case FilterOp.GTE:
                return itemValue >= filterValue;
            case FilterOp.LT:
                return itemValue < filterValue;
            case FilterOp.LTE:
                return itemValue <= filterValue;
            case FilterOp.SW:
                return typeof itemValue === 'string'
                    && typeof filterValue === 'string'
                    && itemValue.toLowerCase().startsWith(filterValue.toLowerCase());
            case FilterOp.EW:
                return typeof itemValue === 'string'
                    && typeof filterValue === 'string'
                    && itemValue.toLowerCase().endsWith(filterValue.toLowerCase());
            case FilterOp.CONT:
                return typeof itemValue === 'string'
                    && typeof filterValue === 'string'
                    && itemValue.toLowerCase().includes(filterValue.toLowerCase());
            case FilterOp.NCONT:
                return typeof itemValue === 'string'
                    && typeof filterValue === 'string'
                    && !itemValue.toLowerCase().includes(filterValue.toLowerCase());
            default:
                return true;
        }
    }

    protected getPropertyByPath(obj: any, path: string): any {
        if (obj == null || !path) return undefined;
        try {
            return path.split('.')
                .reduce((c, k) => (c && c[k] !== undefined ? c[k] : undefined), obj);
        } catch (e) {
            return undefined;
        }
    }
}
